import React, { useEffect, useState } from 'react';
import { doc as firestoreDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase-config.js';
import { ArrowLeft, Building, Calendar, Clock, ExternalLink, Eye, FileText, Lock, LogOut, MessageSquare, Paperclip, Search, User } from 'lucide-react';
import { ChatPainelGenerico } from './ChatPainelGenerico.jsx';

export const AdminPanel = (props) => {
    const { denuncias, adminSearch, setAdminSearch, loggedAdminEmail, handleAdminLogout, updateDenuncia, adminActiveChatId, setAdminActiveChatId } = props;
    
    const [denunciaAtiva, setDenunciaAtiva] = useState(null);
    
    // Novos estados para os filtros
    const [filterStatus, setFilterStatus] = useState('Todos');
    const [filterDataInicio, setFilterDataInicio] = useState('');
    const [filterDataFim, setFilterDataFim] = useState('');

    useEffect(() => {
        if (adminActiveChatId) {
            const unsub = onSnapshot(firestoreDoc(db, 'denuncias', adminActiveChatId), snapshot => {
                setDenunciaAtiva({ id: snapshot.id, ...snapshot.data() });
            });
            return () => unsub();
        } else {
            setDenunciaAtiva(null);
        }
    }, [adminActiveChatId]);

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'Data indisponível';
        if (timestamp.seconds) return new Date(timestamp.seconds * 1000).toLocaleString('pt-BR');
        if (timestamp instanceof Date) return timestamp.toLocaleString('pt-BR');
        return String(timestamp);
    };

    if (denunciaAtiva) {
      return (
        <div className="max-w-6xl mx-auto mt-8 px-4">
          <button onClick={() => setAdminActiveChatId(null)} className="text-blue-600 text-sm font-medium mb-4 flex items-center gap-1 hover:underline"><ArrowLeft size={16} /> Voltar para o Painel Geral</button>
          <ChatPainelGenerico {...props} denuncia={denunciaAtiva} isAdminView={true} />
        </div>
      );
    }

    // Lógica de Filtros Combinados (Busca + Status + Data)
    const denunciasFiltradas = denuncias.filter(d => {
      // 1. Filtro de Texto (Search)
      let matchSearch = true;
      if (adminSearch) {
        const termo = adminSearch.toLowerCase();
        matchSearch = (d.id.toLowerCase().includes(termo) || d.nome.toLowerCase().includes(termo) || d.categoria.toLowerCase().includes(termo) || d.descricao.toLowerCase().includes(termo) || d.status.toLowerCase().includes(termo) || d.setor.toLowerCase().includes(termo));
      }

      // 2. Filtro de Status
      let matchStatus = true;
      if (filterStatus !== 'Todos') {
        matchStatus = d.status === filterStatus;
      }

      // 3. Filtro de Data
      let matchDate = true;
      if (filterDataInicio || filterDataFim) {
        let dateToCompare = null;
        if (d.dataCompleta && d.dataCompleta.toDate) {
            dateToCompare = d.dataCompleta.toDate();
        } else if (d.dataCompleta && d.dataCompleta.seconds) {
            dateToCompare = new Date(d.dataCompleta.seconds * 1000);
        }

        if (dateToCompare) {
            dateToCompare.setHours(0,0,0,0);
            if (filterDataInicio) {
                const start = new Date(filterDataInicio + 'T00:00:00');
                if (dateToCompare < start) matchDate = false;
            }
            if (filterDataFim) {
                const end = new Date(filterDataFim + 'T23:59:59');
                if (dateToCompare > end) matchDate = false;
            }
        }
      }

      return matchSearch && matchStatus && matchDate;
    });

    // Contadores para os Cards
    const total = denuncias.length;
    const abertas = denuncias.filter(d => d.status === 'Em Aberto').length;
    const emAnalise = denuncias.filter(d => d.status === 'Em análise').length;
    const concluidas = denuncias.filter(d => d.status === 'Concluída').length;

    return (
      <div className="max-w-6xl mx-auto mt-8 px-4 animate-fadeIn">
        {/* Cabeçalho */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><Lock size={24} className="text-slate-600" /> Painel de Gestão de Denúncias</h2>
            <p className="text-slate-500 text-sm">Logado como: <strong>{loggedAdminEmail}</strong></p>
          </div>
          <button onClick={handleAdminLogout} className="bg-red-50 hover:bg-red-100 text-red-600 px-4 py-2 rounded-lg text-sm font-medium transition-colors border border-red-200 flex items-center gap-2 shadow-sm">
            <LogOut size={16} /> Sair
          </button>
        </div>

        {/* Cards de Resumo - Clicáveis e Menores */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <button 
            type="button"
            onClick={() => setFilterStatus('Todos')}
            className={`p-2 rounded-lg shadow-sm border flex flex-col items-center justify-center transition-all hover:shadow-md cursor-pointer hover:-translate-y-0.5 ${filterStatus === 'Todos' ? 'bg-slate-100 border-slate-400 ring-1 ring-slate-400' : 'bg-white border-slate-200'}`}
          >
            <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-0.5">Total</span>
            <span className="text-lg font-bold text-slate-800">{total}</span>
          </button>
          
          <button 
            type="button"
            onClick={() => setFilterStatus('Em Aberto')}
            className={`p-2 rounded-lg shadow-sm border flex flex-col items-center justify-center transition-all hover:shadow-md cursor-pointer hover:-translate-y-0.5 ${filterStatus === 'Em Aberto' ? 'bg-red-100 border-red-400 ring-1 ring-red-400' : 'bg-red-50 border-red-200'}`}
          >
            <span className="text-[10px] text-red-600 font-bold uppercase tracking-wider mb-0.5">Abertas</span>
            <span className="text-lg font-bold text-red-700">{abertas}</span>
          </button>
          
          <button 
            type="button"
            onClick={() => setFilterStatus('Em análise')}
            className={`p-2 rounded-lg shadow-sm border flex flex-col items-center justify-center transition-all hover:shadow-md cursor-pointer hover:-translate-y-0.5 ${filterStatus === 'Em análise' ? 'bg-amber-100 border-amber-400 ring-1 ring-amber-400' : 'bg-amber-50 border-amber-200'}`}
          >
            <span className="text-[10px] text-amber-600 font-bold uppercase tracking-wider mb-0.5">Em Análise</span>
            <span className="text-lg font-bold text-amber-700">{emAnalise}</span>
          </button>
          
          <button 
            type="button"
            onClick={() => setFilterStatus('Concluída')}
            className={`p-2 rounded-lg shadow-sm border flex flex-col items-center justify-center transition-all hover:shadow-md cursor-pointer hover:-translate-y-0.5 ${filterStatus === 'Concluída' ? 'bg-emerald-100 border-emerald-400 ring-1 ring-emerald-400' : 'bg-emerald-50 border-emerald-200'}`}
          >
            <span className="text-[10px] text-emerald-600 font-bold uppercase tracking-wider mb-0.5">Concluídas</span>
            <span className="text-lg font-bold text-emerald-700">{concluidas}</span>
          </button>
        </div>

        {/* Barra de Busca e Filtros Avançados */}
        <div className="mb-8 bg-white p-4 rounded-xl shadow-sm border border-slate-200 flex flex-col xl:flex-row gap-4 items-center">
          <div className="flex-1 w-full flex items-center gap-3 bg-slate-50 rounded-lg px-3 py-1 border border-slate-200 focus-within:ring-2 focus-within:ring-blue-500">
            <Search className="text-slate-400 shrink-0" size={20} />
            <input type="text" value={adminSearch} onChange={(e) => setAdminSearch(e.target.value)} placeholder="Buscar por protocolo, nome, categoria ou descrição..." className="w-full p-2 outline-none text-slate-700 bg-transparent text-sm" />
          </div>
          
          <div className="flex flex-wrap items-center gap-3 w-full xl:w-auto">
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="p-2.5 text-sm border border-slate-300 rounded-lg outline-none bg-slate-50 text-slate-700 focus:ring-2 focus:ring-blue-500">
              <option value="Todos">Todos os Status</option>
              <option value="Em Aberto">Em Aberto</option>
              <option value="Em análise">Em análise</option>
              <option value="Concluída">Concluída</option>
            </select>
            
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-300 rounded-lg px-2">
              <span className="text-xs text-slate-400 font-medium pl-1">De</span>
              <input type="date" value={filterDataInicio} onChange={(e) => setFilterDataInicio(e.target.value)} className="p-2 text-sm bg-transparent outline-none text-slate-700" />
              <span className="text-xs text-slate-400 font-medium">Até</span>
              <input type="date" value={filterDataFim} onChange={(e) => setFilterDataFim(e.target.value)} className="p-2 text-sm bg-transparent outline-none text-slate-700" />
            </div>

            {(filterStatus !== 'Todos' || filterDataInicio || filterDataFim || adminSearch) && (
                <button onClick={() => { setFilterStatus('Todos'); setFilterDataInicio(''); setFilterDataFim(''); setAdminSearch(''); }} className="text-sm text-red-500 hover:text-red-700 hover:underline px-2 font-medium">Limpar Filtros</button>
            )}
          </div>
        </div>

        {/* Lista de Denúncias */}
        {denunciasFiltradas.length === 0 ? (
            <div className="bg-white p-12 text-center rounded-xl shadow-sm border border-slate-200">
                <FileText className="mx-auto text-slate-300 mb-4" size={48} />
                <h3 className="text-lg font-medium text-slate-600">Nenhuma denúncia encontrada com os filtros atuais.</h3>
            </div>
        ) : (
            <div className="grid gap-4">
                {denunciasFiltradas.map((d) => (
                    <div key={d.id} className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 flex flex-col lg:flex-row gap-6 transition-all hover:shadow-md">
                        <div className="flex-1 space-y-3">
                            <div className="flex flex-wrap items-center gap-3">
                                <span className="font-mono bg-slate-100 text-slate-700 px-2 py-1 rounded text-sm font-bold border border-slate-200">{d.id}</span>
                                <span className="text-xs text-slate-500 bg-slate-50 px-2 py-1 rounded border border-slate-100 flex items-center gap-1"><Calendar size={12}/> {formatTimestamp(d.dataCompleta)}</span>
                                <span className="px-2 py-1 rounded text-xs font-bold uppercase bg-blue-50 text-blue-700 border border-blue-100">{d.categoria}</span>
                            </div>
                            <p className="text-slate-700 text-sm line-clamp-3 bg-slate-50 p-3 rounded border border-slate-100">{d.descricao}</p>
                            {d.arquivos && d.arquivos.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {d.arquivos.map((arq, idx) => (<a key={idx} href={arq.url} target="_blank" rel="noopener noreferrer" className="text-[11px] bg-blue-50 text-blue-600 hover:bg-blue-100 px-2 py-1 rounded flex items-center gap-1 border border-blue-200 transition-colors"><Paperclip size={10}/> {arq.nomeExibicao} <ExternalLink size={10}/></a>))}
                                </div>
                            )}
                            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 pt-2 border-t border-slate-100 mt-2">
                                {d.anonimo ? (
                                    <span className="bg-slate-200 text-slate-700 px-2 py-1 rounded-full flex items-center gap-1"><Eye size={12}/> Denúncia Anônima</span>
                                ) : (
                                    <span className="bg-amber-100 text-amber-800 px-2 py-1 rounded-full flex items-center gap-1"><User size={12}/> {d.nome} | {d.email} | {d.telefone}</span>
                                )}
                            </div>
                        </div>
                        <div className="w-full lg:w-64 flex flex-col gap-3 bg-slate-50 p-4 rounded-lg border border-slate-100">
                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase flex items-center gap-1 mb-1"><Clock size={12} /> Status</label>
                                <select value={d.status} onChange={(e) => updateDenuncia(d.id, 'status', e.target.value)} className={`w-full p-2 text-sm border rounded outline-none font-medium cursor-pointer ${d.status === 'Em Aberto' ? 'bg-red-50 border-red-200 text-red-700' : d.status === 'Em análise' ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-emerald-50 border-emerald-200 text-emerald-700'}`}>
                                    <option value="Em Aberto">Em Aberto</option>
                                    <option value="Em análise">Em análise</option>
                                    <option value="Concluída">Concluída</option>
                                </select>
                                {d.status === 'Em Aberto' && <p className="text-[10px] text-red-500 mt-1">Altere para "Em análise" para habilitar o chat.</p>}
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase flex items-center gap-1 mb-1"><Building size={12} /> Local / Setor</label>
                                <select value={d.setor} onChange={(e) => updateDenuncia(d.id, 'setor', e.target.value)} className="w-full p-2 text-sm border border-slate-300 rounded outline-none bg-white text-slate-700 cursor-pointer">
                                    <option value="Não Atribuído">Não Atribuído</option>
                                    <option value="RH">RH</option>
                                    <option value="INMETRO">INMETRO</option>
                                    <option value="Direção">Direção</option>
                                    <option value="Devolvida">Devolvida</option>
                                </select>
                            </div>
                            <button onClick={() => setAdminActiveChatId(d.id)} className="mt-2 w-full bg-slate-800 hover:bg-slate-900 text-white text-xs py-2 rounded flex justify-center items-center gap-1 transition-colors shadow-sm">
                                <MessageSquare size={12} /> {d.status === 'Em análise' ? 'Responder Relator' : 'Ver Detalhes'}
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        )}
      </div>
    );
};
